import { Box, Link, Textarea, useDisclosure, useToast } from '@chakra-ui/react'
import React, { useState } from 'react'
import { IAction } from '../../../components/table/ActionsDropdown'
import { Table, TableDataProp } from '../../../components/table/Table'
import { Plan } from '../../../types'
import { CheckIcon } from '@chakra-ui/icons'
import { useRole } from '../../../utils/rbac'
import { disablePlan } from '../../../api'
import { useAuth } from '../../../context/auth-context'
import { GenericAlertDialog } from '../Components/GenericAlertDialog'
import { nextTick } from 'process'

interface IInputParams {
  plans: Plan[]
  loading: boolean
  editPlanAction: IAction
  onPlanHidden: (plan: Plan) => void
}

export const Plans: React.FC<IInputParams> = (input) => {
  const { plans, loading, editPlanAction, onPlanHidden } = input

  const { currentOrgUnit } = useAuth()
  const { isSpaceSuperAdminRole } = useRole()
  const [isConfirming, setIsConfirming] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>()
  const { isOpen, onClose, onOpen } = useDisclosure()

  const reasonRef = React.useRef<HTMLTextAreaElement>(null)
  const toast = useToast()

  function RenderPlanLink(plan: Plan) {
    return (
      <Link color="#255CE1" onClick={() => editPlanAction.action(plan.id)}>
        {plan.name}
      </Link>
    )
  }

  const tableData = plans.map((plan) => {
    const actions: IAction[] = []

    const cleanedData = {
      id: TableDataProp(plan.id),
      test_prefix: TableDataProp(`plan-row-${plan.id}`),
      name: TableDataProp(RenderPlanLink(plan), plan.name),
      rate: TableDataProp(plan.rate_string),
      meeting_room_credits_included: TableDataProp(
        plan.meeting_room_credits_included
      ),
      plan_category: TableDataProp(plan?.category?.name),
      // when a plan is not hidden it's going to appear as "show to members" to make it more user friendly
      // when a plan is not hidden to admin is going to appear as "show to admin"
      hidden: TableDataProp(!plan.hidden && <CheckIcon />),
      hidden_to_admin: TableDataProp(!plan.hidden_to_admin && <CheckIcon />),
      approve_on_sign_up: TableDataProp(
        plan.approve_on_sign_up && <CheckIcon />
      ),
      addons: TableDataProp(plan.addon_plan && <CheckIcon />),
      actions: TableDataProp([])
    }

    // Define actions for the row
    actions.push(editPlanAction)

    if (isSpaceSuperAdminRole) {
      actions.push({
        text: 'Remove plan',
        testIdPrefix: `plan-row-${plan.id}-remove`,
        action: (id: number) => {
          setSelectedPlan(plans.find((plan) => plan.id === id))
          onOpen()
          nextTick(() => {
            reasonRef.current?.focus()
          })
        }
      })
    }

    cleanedData.actions = TableDataProp(actions)

    return cleanedData
  })

  const handleDisablePlanConfirm = () => {
    if (isConfirming || !selectedPlan || !currentOrgUnit?.id) return
    const reason = reasonRef.current?.value
    if (!reason || reason.trim() === '') {
      console.error('Reason for disabling the plan is required.')
      toast({
        title: 'Error',
        description: 'Please provide a reason for disabling the plan.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      return
    }
    setIsConfirming(true)
    disablePlan(selectedPlan.id, reason, currentOrgUnit.id)
      .then((res) => {
        setIsConfirming(false)
        setSelectedPlan(undefined)
        onClose()
        toast({
          title: 'Plan Disabled',
          description: `Plan ${selectedPlan.name} has been successfully disabled.`,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onPlanHidden(selectedPlan)
      })
      .catch((error) => {
        toast({
          title: 'Error Disabling Plan',
          description: `Failed to disable plan ${selectedPlan.name}. Please try again later.`,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        setIsConfirming(false)
      })
  }

  return (
    <Box my="3" w="100%" minWidth="955px">
      <Table
        headers={[
          { header: 'id', accessor: 'id', show: false },
          { header: 'test_prefix', accessor: 'test_prefix', show: false },
          {
            header: 'Plan',
            accessor: 'name',
            show: true,
            width: 5 / 12,
            enableFilter: true
          },
          {
            header: 'Rate',
            accessor: 'rate',
            show: true,
            width: 2 / 12
          },
          {
            header: 'Meeting Credits',
            accessor: 'meeting_room_credits_included',
            show: true,
            width: 1.5 / 12
          },
          {
            header: 'Add-ons',
            accessor: 'addons',
            show: true,
            width: 1 / 12
          },
          {
            header: 'Plan Category',
            accessor: 'plan_category',
            show: true,
            width: 1 / 12
          },
          {
            header: 'Show to members',
            accessor: 'hidden',
            show: true,
            width: 1 / 12
          },
          {
            header: 'Show to admin',
            accessor: 'hidden_to_admin',
            show: true,
            width: 1 / 12
          },
          {
            header: 'Approve on Sign Up',
            accessor: 'approve_on_sign_up',
            show: true,
            width: 1 / 12
          },

          { header: '', accessor: 'actions', show: true, width: 1 / 12 }
        ]}
        data={tableData}
        loading={loading}
      />

      <GenericAlertDialog
        title={`Disable Plan: ${selectedPlan?.name}`}
        description="Please, give a reason for disabling this plan."
        isOpen={isOpen}
        onClose={() => {
          setIsConfirming(false)
          setSelectedPlan(undefined)
          onClose()
        }}
        isConfirming={isConfirming}
        onConfirm={handleDisablePlanConfirm}
        confirmButtonText="Disable Plan"
      >
        <Box>
          <Textarea
            ref={reasonRef}
            maxLength={500}
            placeholder="Enter the reason for disabling this plan..."
          ></Textarea>
        </Box>
      </GenericAlertDialog>
    </Box>
  )
}
